import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Avatar,
  Container,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon
} from '@mui/icons-material';

// Importar los datos del JSON
import prestacionesAuxiliaresData from '../../login_logs_prestaciones_auxiliares_valenciana_reduced.json';
import praconServiextData from '../../login_logs_pracon_serviext_reduced.json';
import telimanData from '../../login_logs_teliman_reduced.json';
import scuPracomAndaluciaData from '../../login_logs_scu_pracom_andalucia_reduced.json';

const LogDetail = () => {
  const { company, logId } = useParams();
  const navigate = useNavigate();
  const [logData, setLogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLogDetail();
  }, [company, logId]);

  const loadLogDetail = () => {
    setLoading(true);
    try {
      // Seleccionar el dataset correcto
      let dataSource;
      if (company === 'pracon-serviext') {
        dataSource = praconServiextData;
      } else if (company === 'prestaciones-auxiliares') {
        dataSource = prestacionesAuxiliaresData;
      } else if (company === 'teliman') {
        dataSource = telimanData;
      } else if (company === 'scu-pracom-andalucia') {
        dataSource = scuPracomAndaluciaData;
      } else {
        setError('Empresa no válida');
        setLoading(false);
        return;
      }

      // Buscar el log específico por ID
      const log = dataSource.login_logs.find(log => log._id === logId);
      
      if (!log) {
        setError('Log no encontrado');
        setLoading(false);
        return;
      }

      // Procesar los datos del log
      const processedLog = {
        id: log._id,
        email: log.email,
        timestamp: log.timestamp,
        ipAddress: log.ip_address,
        userAgent: log.user_agent,
        sessionId: log.session_id,
        userId: log.user_id,
        source: log.source,
        level: log.level,
        content: log.content,
        extractedAt: log._extracted_at,
        fullDocument: log.full_document,
        // Campos calculados
        deviceType: getDeviceType(log.user_agent),
        browser: getBrowser(log.user_agent),
        os: getOperatingSystem(log.user_agent),
        isMobile: isMobileDevice(log.user_agent),
        date: new Date(log.timestamp).toLocaleDateString('es-ES'),
        time: new Date(log.timestamp).toLocaleTimeString('es-ES'),
        status: 'success',
        // URL completa
        detailUrl: `https://www.test-sentry.bepart-innova.com/logs/${company}/${log._id}`,
        companyName: dataSource.empresa_nombre,
        companyCode: dataSource.empresa
      };

      setLogData(processedLog);
    } catch (error) {
      console.error('Error loading log detail:', error);
      setError('Error al cargar los detalles del log');
    } finally {
      setLoading(false);
    }
  };

  // Funciones auxiliares para procesar user agent
  const getDeviceType = (userAgent) => {
    if (userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android')) {
      return 'Mobile';
    } else if (userAgent.includes('Tablet') || userAgent.includes('iPad')) {
      return 'Tablet';
    }
    return 'Desktop';
  };

  const getBrowser = (userAgent) => {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  };

  const getOperatingSystem = (userAgent) => {
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac OS X')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('iPhone OS')) return 'iOS';
    if (userAgent.includes('Android')) return 'Android';
    return 'Unknown';
  };

  const isMobileDevice = (userAgent) => {
    return userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android');
  };

  const getDeviceColor = (deviceType) => {
    switch (deviceType) {
      case 'Mobile':
        return 'primary';
      case 'Tablet':
        return 'secondary';
      case 'Desktop':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleBack = () => {
    navigate('/logs');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ padding: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Typography>Cargando detalles del log...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !logData) {
    return (
      <Container maxWidth="lg" sx={{ padding: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Log no encontrado'}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Volver a la lista
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ padding: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Volver a la lista
        </Button>
        
        <Typography variant="h4" component="h1" sx={{ color: '#1976d2', fontWeight: 600, mb: 1 }}>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Detalles del Log de Inicio de Sesión
        </Typography>
        
        <Typography variant="body1" color="textSecondary">
          Información detallada del acceso al sistema
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Main Info */}
        <Grid item xs={12} md={8}>
          {/* User Information */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Información del Usuario
              </Typography>
              
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                  {logData.email.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{logData.email}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Usuario ID: {logData.userId}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Email</Typography>
                    <Typography variant="body1" fontWeight="medium">{logData.email}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">ID de Usuario</Typography>
                    <Typography variant="body1" fontWeight="medium">{logData.userId}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">ID de Sesión</Typography>
                    <Typography variant="body1" fontFamily="monospace" fontSize="12px">
                      {logData.sessionId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Estado</Typography>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Login Exitoso"
                      color="success"
                      variant="filled"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Device Information */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Información del Dispositivo
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Tipo de Dispositivo</Typography>
                    <Chip
                      label={logData.deviceType}
                      color={getDeviceColor(logData.deviceType)}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Navegador</Typography>
                    <Typography variant="body1" fontWeight="medium">{logData.browser}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Sistema Operativo</Typography>
                    <Typography variant="body1" fontWeight="medium">{logData.os}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Es Móvil</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {logData.isMobile ? 'Sí' : 'No'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Technical Details */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                Detalles Técnicos
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Dirección IP</Typography>
                    <Typography variant="body1" fontFamily="monospace" fontWeight="medium">
                      {logData.ipAddress}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Fuente</Typography>
                    <Typography variant="body1" fontWeight="medium">{logData.source}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">User Agent</Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ 
                      wordBreak: 'break-all',
                      backgroundColor: '#f5f5f5',
                      padding: 1,
                      borderRadius: 1,
                      fontSize: '12px'
                    }}>
                      {logData.userAgent}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Timestamps & URL */}
        <Grid item xs={12} md={4}>
          {/* Timestamps */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Fechas y Horas
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Fecha de Acceso</Typography>
                <Typography variant="body1" fontWeight="medium">{logData.date}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Hora de Acceso</Typography>
                <Typography variant="body1" fontWeight="medium">{logData.time}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Extraído el</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(logData.extractedAt).toLocaleString('es-ES')}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* URL Access */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                <OpenInNewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                URL de Acceso Directo
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Enlace directo a este log:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: 1
                }}>
                  <Typography 
                    variant="body2" 
                    fontFamily="monospace" 
                    sx={{ 
                      wordBreak: 'break-all',
                      fontSize: '12px',
                      color: '#1976d2'
                    }}
                  >
                    {logData.detailUrl}
                  </Typography>
                </Box>
                <Box display="flex" gap={1} mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(logData.detailUrl, '_blank')}
                  >
                    Abrir en Nueva Pestaña
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                Información de la Empresa
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Empresa</Typography>
                <Typography variant="body1" fontWeight="medium">{logData.companyName}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Código de Empresa</Typography>
                <Typography variant="body1" fontWeight="medium">{logData.companyCode}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default LogDetail;
